import { ApolloError, useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { ReactElement, useRef, useState } from 'react'
import styled from 'styled-components'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { FilePreview, InputFile, InputFileContainer } from 'components/v2/InputFile'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { Select } from 'components/v2/Select'
import { CREATE_ROUTING_PROJECT_DATASET, CREATE_ROUTING_PROJECT_PLAN } from 'features/RoutingProjects/mutations'
import {
  ICreateRoutingProjectPlanForm,
  IRoutingProjectModel,
  IRoutingProjectPlan
} from 'features/RoutingProjects/types'
import { ROUTING_PROJECT_PLAN_FRAGMENT, ROUTING_PROJECT_PLAN_FRAGMENT_NAME } from 'features/RoutingProjects/queries'
import { addDatasetToRoutingProject } from 'features/RoutingProjects/cache'
import { RoutingProjectDatasetFragment, RoutingProjectPlanStatus } from 'gql/generated/graphql'
import { getCSVErrors, hasCSVError } from 'features/RoutingProjects/utils'
import { getUnprocessableErrors, isAtLeastOneLocationWithAllWeekdaysError } from 'utils/error'
import { getFileNameWithoutExtension, usePushFileToS3 } from 'utils/file'
import { schema } from './schema'

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
`

const SplitLine = styled.div`
  border-top: 1px solid #b0b0b0;
`

const SplitLabel = styled.div`
  color: #b0b0b0;
  font-size: 14px;
  line-height: 130%;
  font-weight: 400;
`

const messages = defineMessages({
  namePlaceholder: {
    id: 'routingProjects.create.plan.name.placeholder',
    defaultMessage: 'Enter plan name'
  },
  datasetPlaceholder: {
    id: 'routingProjects.create.plan.dataset.placeholder',
    defaultMessage: 'select an option'
  },
  datasetNamePlaceholder: {
    id: 'routingProjects.create.plan.dataset.name.placeholder',
    defaultMessage: 'Enter dataset name'
  }
})

interface ICreatePlanModalProps {
  routingProjectModel: IRoutingProjectModel
  closeModal: () => void
  onSuccess: (plan: IRoutingProjectPlan) => void
}

export const CreatePlanModal = ({
  routingProjectModel,
  closeModal,
  onSuccess
}: ICreatePlanModalProps): ReactElement | null => {
  const intl = useIntl()
  const pushFileToS3 = usePushFileToS3()
  const newDatasetNameRef = useRef<string>('')
  const newPlanRef = useRef<ICreateRoutingProjectPlanForm | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [s3Error, setS3Error] = useState<Error | null>(null)
  const [createDataset, { error: createDatasetError }] = useMutation(CREATE_ROUTING_PROJECT_DATASET, {
    update(_, { data }) {
      const newDataset = data?.createRoutingProjectDataset
      if (newDataset) {
        addDatasetToRoutingProject({
          routingProjectId: routingProjectModel.id,
          datasetId: newDataset.id,
          datasetName: newDatasetNameRef.current
        })
      }
    }
  })

  const updateSelectedPlan = async (plan: IRoutingProjectPlan): Promise<void> => {
    setTimeout(() => {
      setIsSubmitting(false)
      closeModal()
      onSuccess(plan)
    }, 2000)
  }
  const [createPlan, { error: createPlanError, reset }] = useMutation(CREATE_ROUTING_PROJECT_PLAN, {
    onCompleted(data) {
      updateSelectedPlan({
        id: data.createRoutingProjectPlan.id,
        name: newPlanRef.current?.name || '',
        status: RoutingProjectPlanStatus.Simulation,
        dataset: {
          name: newPlanRef.current?.dataset.name || '',
          locations: [],
          hasDow: true
        },
        constraints: {},
        optimizations: []
      })
    },
    update(cache, { data }) {
      const existingDataset = routingProjectModel.datasets.find(
        (dataset) => dataset.id === newPlanRef.current?.dataset.id
      )
      cache.modify({
        id: cache.identify({
          __typename: 'RoutingProject',
          id: routingProjectModel.id
        }),
        fields: {
          plans(existingPlans = []) {
            const newRoutingProjectPlanRef = cache.writeFragment({
              data: {
                __typename: 'RoutingProjectPlan',
                id: data?.createRoutingProjectPlan.id || '',
                name: newPlanRef.current?.name || '',
                status: RoutingProjectPlanStatus.Simulation,
                dataset: {
                  __typename: 'RoutingProjectDataset',
                  id: existingDataset?.id || '',
                  name: existingDataset?.name || '',
                  emptyDow: existingDataset?.hasDow || false,
                  locations:
                    existingDataset?.locations?.map((location) => ({
                      __typename: 'RoutingProjectDatasetLocation',
                      id: location.id,
                      label: location.label,
                      coordinates: {
                        __typename: 'RoutingProjectDatasetLocationCoordinates',
                        latitude: location.coordinates.latitude.toString(),
                        longitude: location.coordinates.longitude.toString()
                      },
                      dow: location.dows
                    })) || []
                } as RoutingProjectDatasetFragment,
                constraints: null,
                optimizations: []
              },
              fragment: ROUTING_PROJECT_PLAN_FRAGMENT,
              fragmentName: ROUTING_PROJECT_PLAN_FRAGMENT_NAME
            })
            return [newRoutingProjectPlanRef, ...existingPlans]
          }
        }
      })
    }
  })

  const handleSubmit = async (values: ICreateRoutingProjectPlanForm): Promise<void> => {
    setIsSubmitting(true)
    newPlanRef.current = values
    let path = ''

    if (values.file) {
      try {
        path = await pushFileToS3(values.file)
      } catch (error) {
        setS3Error(error as Error)
        setIsSubmitting(false)
        return
      }
      try {
        newDatasetNameRef.current = values.dataset.name || ''
        const { data } = await createDataset({
          variables: {
            input: {
              id: routingProjectModel.id,
              dataset: {
                name: values.dataset?.name || '',
                url: path
              }
            }
          }
        })
        newPlanRef.current.dataset.id = data?.createRoutingProjectDataset.id || ''
      } catch (error) {
        setIsSubmitting(false)
        return
      }
    }
    try {
      await createPlan({
        variables: {
          input: {
            id: routingProjectModel.id,
            plan: {
              name: values.name,
              dataset: {
                id: newPlanRef.current.dataset.id
              }
            }
          }
        }
      })
    } catch (ex) {
      setIsSubmitting(false)
    }
  }

  const initialValues: ICreateRoutingProjectPlanForm = {
    name: '',
    file: '',
    dataset: {
      id: '',
      name: ''
    }
  }

  const renderErrorMessage = (
    createDataSetError?: ApolloError,
    createPlanError?: ApolloError,
    s3Error?: Error | null
  ): ReactElement | null => {
    if (s3Error) {
      return (
        <ErrorMessage>
          <FormattedMessage
            id="routingProjects.create.dataset.s3.error"
            defaultMessage="An error occurred while uploading the file. Please try again."
          />
        </ErrorMessage>
      )
    }
    if (createDataSetError) {
      if (isAtLeastOneLocationWithAllWeekdaysError(createDataSetError)) {
        return (
          <ErrorMessage>
            <FormattedMessage
              id="routingProjects.create.dataset.allWeekdays.error"
              defaultMessage="It is essential that you configure a stop every day in your dataset."
            />
          </ErrorMessage>
        )
      } else if (hasCSVError(createDataSetError)) {
        return <ErrorMessage>{getCSVErrors(createDataSetError, intl)}</ErrorMessage>
      } else {
        return (
          <ErrorMessage errors={getUnprocessableErrors(createDataSetError)}>
            <FormattedMessage
              id="routingProjects.create.dataset.error"
              defaultMessage="An error occurred while uploading the dataset. Please try again."
            />
          </ErrorMessage>
        )
      }
    }
    if (createPlanError) {
      return (
        <ErrorMessage errors={getUnprocessableErrors(createPlanError)}>
          <FormattedMessage
            id="routingProjects.create.plan.error"
            defaultMessage="An error occurred while creating the plan"
          />
        </ErrorMessage>
      )
    }
    return null
  }

  return (
    <Modal.Container>
      <Modal.Header smallMarginBottom>
        <Heading.H5>
          <FormattedMessage id="routingProjects.create.plan.title" defaultMessage="Create Plan" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, setValues, handleBlur, touched, errors, values } = props

          const handleFileChange = async (file: File | ''): Promise<void> => {
            reset()
            setS3Error(null)
            if (!file) {
              setValues({ ...values, file: '' })
              return
            } else {
              setValues({
                ...values,
                file,
                dataset: {
                  id: '',
                  name: getFileNameWithoutExtension(file)
                }
              })
            }
          }

          const handleDatasetChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
            const datasetSelected = routingProjectModel.datasets.find((dataset) => dataset.id === value)
            setValues({
              ...values,
              dataset: {
                id: datasetSelected?.id || '',
                name: datasetSelected?.name || ''
              },
              file: ''
            })
          }

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Input
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.name}
                      errors={errors.name}
                      label={
                        <FormattedMessage id="routingProjects.create.plan.name.label" defaultMessage="Plan name" />
                      }
                      placeholder={intl.formatMessage(messages.namePlaceholder)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Select
                      id="dataset"
                      value={values.dataset.id}
                      onChange={handleDatasetChange}
                      onBlur={handleBlur}
                      touched={touched.dataset?.id}
                      errors={errors.dataset?.id}
                      label={
                        <FormattedMessage id="routingProjects.create.plan.dataset.label" defaultMessage="Dataset" />
                      }
                    >
                      <option value="">{intl.formatMessage(messages.datasetPlaceholder)}</option>
                      {routingProjectModel.datasets.map((dataset) => (
                        <option key={dataset.id} value={dataset.id}>
                          {dataset.name}
                        </option>
                      ))}
                    </Select>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SplitGrid>
                      <SplitLine />
                      <SplitLabel>
                        <FormattedMessage id="dictionary.or" defaultMessage="or" />
                      </SplitLabel>
                      <SplitLine />
                    </SplitGrid>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <InputFileContainer>
                      <InputFile
                        touched={touched.file}
                        errors={errors.file}
                        onChange={handleFileChange}
                        accept={{
                          'text/csv': []
                        }}
                      />
                      {!isEmpty(values.file) && (
                        <FilePreview
                          file={values.file as File}
                          filename={(values.file as File).name}
                          onRemoveFile={(): void => {
                            reset()
                            setS3Error(null)
                            setValues({
                              ...values,
                              file: '',
                              dataset: {
                                id: '',
                                name: ''
                              }
                            })
                          }}
                        />
                      )}
                    </InputFileContainer>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id="dataset.name"
                      value={!isEmpty(values.file) ? values.dataset.name : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.dataset?.name}
                      errors={!isEmpty(values.file) ? errors.dataset?.name : undefined}
                      label={
                        <FormattedMessage id="routingProjects.create.plan.name.label" defaultMessage="Dataset name" />
                      }
                      placeholder={intl.formatMessage(messages.datasetNamePlaceholder)}
                      disabled={isEmpty(values.file)}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {!isSubmitting && (s3Error || createDatasetError || createPlanError) && (
                  <Modal.ErrorMessage>
                    {renderErrorMessage(createDatasetError, createPlanError, s3Error)}
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="routingProjects.create.plan.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting}>
                    <FormattedMessage id="routingProjects.create.plan.submit" defaultMessage="Create" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
